import L from "leaflet";
import { TRACKER_SVG } from "./mapConstants";

// Ikon cache for at undgå at oprette nye ikoner hver gang
const iconCache = new Map();

// Beregn ikonstørrelse baseret på zoom-niveau
// Ikoner vokser ved indzoom (op til maxScale 1.1 ved zoom 18) så de er
// tydeligere når man er zoomet helt ind — men uden at fylde for meget.
// minScale 0.7 holder dem synlige ved udzoom.
export function getIconScale(zoom) {
  const minZoom = 13, maxZoom = 18;
  const minScale = 0.7, maxScale = 1.1;
  const t = Math.min(1, Math.max(0, (zoom - minZoom) / (maxZoom - minZoom)));
  return minScale + (maxScale - minScale) * t;
}

// Mini-symboler per type til mobil (enkle SVG'er der er hurtige at rendere)
const MOBILE_TYPE_SYMBOLS = {
  ressourceø: '♻',
  ressourcespand: '●',
  maxicontainer: '■',
  opmærksomhedspunkt: '⚠',
  drikkevand: '💧',
  pausetelt: '▲',
  toilet: 'T',
  pissoir: 'P',
  food: 'F',
  drinks: 'D',
};

const MOBILE_TYPE_BORDERS = {
  ressourceø: 'white',
  ressourcespand: '#3b82f6',
  maxicontainer: 'white',
  opmærksomhedspunkt: '#ef4444',
  drikkevand: 'white',
  pausetelt: 'white',
  toilet: 'white',
  pissoir: 'white',
  food: 'white',
  drinks: 'white',
};

const MOBILE_TYPE_BG = {
  drikkevand: '#3b82f6',
  pausetelt: '#8b5cf6',
  toilet: '#14b8a6',
  pissoir: '#ec4899',
  food: '#6b7280',
  drinks: '#374151',
};

// Hurtig mobil-ikon: 1 DOM-element, ingen billeder, ingen filtre
export const createMobileIcon = (type, status = "grøn", binFraction = null, setupStatus = null, bins = null, zoom = 16, operational = false) => {
  const cacheKey = `mob-${type}-${status}-${setupStatus}-${binFraction}-op${operational}-z${zoom}`;
  if (iconCache.has(cacheKey)) return iconCache.get(cacheKey);
  
  const scale = getIconScale(zoom);
  const effectiveStatus = setupStatus || status;
  
  // Status-farve
  let bgColor;
  if (MOBILE_TYPE_BG[type]) {
    bgColor = MOBILE_TYPE_BG[type];
  } else if (effectiveStatus === 'rød' || effectiveStatus === 'pending' || effectiveStatus === 'red' || effectiveStatus === 'out_of_service') {
    bgColor = '#ef4444';
  } else if (effectiveStatus === 'gul' || effectiveStatus === 'in_progress' || effectiveStatus === 'yellow' || effectiveStatus === '75_pct') {
    bgColor = '#FFD400';
  } else {
    bgColor = '#22c55e';
  }
  
  // "Sæt i drift": turkis = i drift/OK, lilla = tømning bestilt (afventer/i gang)
  if (operational && (type === 'ressourceø' || type === 'ressourcespand')) {
    const pending = (status === 'rød' || status === 'red' || status === 'pending' ||
                     status === 'gul' || status === 'yellow' || status === 'in_progress');
    bgColor = pending ? '#a855f7' : '#14b8a6';
  }
  
  const borderColor = MOBILE_TYPE_BORDERS[type] || 'white';
  const symbol = MOBILE_TYPE_SYMBOLS[type] || '●';
  // Mobile ikon-størrelse forhøjet fra 22/18 → 30/26 så ikoner er bedre synlige på tablet/mobil
  const size = Math.round((type === 'ressourcespand' ? 26 : 30) * scale);
  const fontSize = Math.round(size * 0.5);
  const borderW = Math.max(1, Math.round(2 * scale));
  
  // Opmærksomhedspunkt — bevar trekant
  if (type === 'opmærksomhedspunkt') {
    const attSize = Math.round(30 * scale);
    const html = `<div style="width:${attSize}px;height:${attSize}px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.5));">
      <svg width="${attSize}" height="${attSize}" viewBox="0 0 24 24"><path d="M12 2L1 21h22L12 2z" fill="#1a1a1a" stroke="#000" stroke-width="0.5"/><text x="12" y="17" text-anchor="middle" font-size="12" font-weight="bold" fill="#ef4444">!</text></svg></div>`;
    const icon = L.divIcon({ html, className: 'custom-marker mob', iconSize: [attSize, attSize], iconAnchor: [attSize / 2, attSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }

  // Toilet — firkantet hvidt skilt med sort mand+kvinde-silhouette (WC-skiltet)
  if (type === 'toilet') {
    const tSize = Math.round(30 * scale);
    const html = `<div style="width:${tSize}px;height:${tSize}px;background:#fff;border:${Math.max(1,Math.round(1.5*scale))}px solid #000;border-radius:${Math.max(2,Math.round(3*scale))}px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.4);pointer-events:none;cursor:pointer;">
      <svg width="${Math.round(tSize*0.78)}" height="${Math.round(tSize*0.78)}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <!-- Kvinde til venstre -->
        <circle cx="30" cy="15" r="9" fill="#000"/>
        <path d="M20 26 L40 26 L46 62 L37 62 L37 88 L23 88 L23 62 L14 62 Z" fill="#000"/>
        <!-- Mand til højre -->
        <circle cx="70" cy="15" r="9" fill="#000"/>
        <rect x="60" y="26" width="20" height="35" rx="2" fill="#000"/>
        <rect x="62" y="59" width="7" height="30" fill="#000"/>
        <rect x="71" y="59" width="7" height="30" fill="#000"/>
      </svg></div>`;
    const icon = L.divIcon({ html, className: 'custom-marker toilet-marker', iconSize: [tSize, tSize], iconAnchor: [tSize / 2, tSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }

  // Pissoir — firkantet hvidt skilt med sort mand-ved-urinal silhouette
  if (type === 'pissoir') {
    const pSize = Math.round(30 * scale);
    const html = `<div style="width:${pSize}px;height:${pSize}px;background:#fff;border:${Math.max(1,Math.round(1.5*scale))}px solid #000;border-radius:${Math.max(2,Math.round(3*scale))}px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.4);pointer-events:none;cursor:pointer;">
      <svg width="${Math.round(pSize*0.78)}" height="${Math.round(pSize*0.78)}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <!-- Væg + urinal på venstre side -->
        <rect x="10" y="8" width="6" height="80" fill="#000"/>
        <path d="M18 40 L36 40 Q40 40 40 44 L40 62 Q40 70 32 74 L18 74 Z" fill="#000"/>
        <!-- Mand til højre -->
        <circle cx="66" cy="20" r="9" fill="#000"/>
        <path d="M56 30 L76 30 L76 63 L70 63 L70 88 L60 88 L60 63 L56 63 Z" fill="#000"/>
        <!-- Stream/dråber -->
        <circle cx="50" cy="55" r="1.5" fill="#000"/>
        <circle cx="47" cy="60" r="1.2" fill="#000"/>
        <circle cx="43" cy="64" r="1" fill="#000"/>
      </svg></div>`;
    const icon = L.divIcon({ html, className: 'custom-marker pissoir-marker', iconSize: [pSize, pSize], iconAnchor: [pSize / 2, pSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }
  
  const html = `<div style="background:${bgColor};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:${borderW}px solid ${borderColor};box-shadow:0 1px 3px rgba(0,0,0,0.3);color:white;font-size:${fontSize}px;line-height:1;">${symbol}</div>`;
  const icon = L.divIcon({ html, className: 'custom-marker mob', iconSize: [size, size], iconAnchor: [size / 2, size] });
  iconCache.set(cacheKey, icon);
  return icon;
};

export const createCustomIcon = (type, status = "grøn", binFraction = null, setupStatus = null, bins = null, zoom = 16, containers = null, markers = null, operational = false) => {
  const scale = getIconScale(zoom);
  const markerKey = markers && markers.length ? markers.map(m => `${m.symbol}:${m.color}`).join('|') : 'nm';
  const cacheKey = `${type}-${status}-${binFraction}-${setupStatus}-op${operational}-${bins ? JSON.stringify(bins) : 'null'}-${containers ? 'dz' + containers.some(c => (c.fraction_type || '').toLowerCase() === 'dropzone') + 'ou' + containers.some(c => (c.fraction_type || '').toLowerCase() === 'tømmes udenfor afvikling') : 'null'}-${markerKey}-z${zoom}`;
  
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }
  
  let iconUrl = '';
  let bgColor = '#22c55e';
  let hasBlueRing = false;
  
  const effectiveStatus = setupStatus || status;
  
  if (effectiveStatus === 'rød' || effectiveStatus === 'pending' || effectiveStatus === 'red') bgColor = '#ef4444';
  else if (effectiveStatus === 'gul' || effectiveStatus === 'in_progress' || effectiveStatus === 'yellow') bgColor = '#FFD400';
  else if (effectiveStatus === '75_pct') bgColor = '#f97316';
  else if (effectiveStatus === 'grøn' || effectiveStatus === 'green') bgColor = '#22c55e';
  
  if (type === 'ressourceø') {
    iconUrl = 'https://customer-assets.emergentagent.com/job_festival-cleanup/artifacts/56pkmehq_pngwing.com.png';
    bgColor = (status === 'grøn' || status === 'green') ? '#22c55e' : (status === 'gul' || status === 'yellow') ? '#FFD400' : '#ef4444';
    if (setupStatus) {
      bgColor = setupStatus === 'green' ? '#22c55e' : setupStatus === 'yellow' ? '#FFD400' : '#ef4444';
    }
  } else if (type === 'toilet') {
    iconUrl = 'SVG_TOILET'; bgColor = '#14b8a6';
  } else if (type === 'pissoir') {
    iconUrl = 'SVG_PISSOIR'; bgColor = '#ec4899';
  } else if (type === 'opmærksomhedspunkt') {
    iconUrl = 'SVG_ATTENTION'; bgColor = 'transparent';
  } else if (type === 'maxicontainer') {
    iconUrl = 'https://customer-assets.emergentagent.com/job_fest-cleanup/artifacts/ew3gbvpw_container.png';
    if (status === 'out_of_service') bgColor = '#ef4444';
    else if (status === 'pending') bgColor = '#ef4444';
    else if (status === '75_pct') bgColor = '#f59e0b';
    else if (status === 'in_progress') bgColor = '#FFD400';
    else bgColor = '#22c55e';
  } else if (type === 'ressourcespand') {
    iconUrl = ''; hasBlueRing = true;
    if (setupStatus) {
      bgColor = setupStatus === 'green' ? '#22c55e' : setupStatus === 'yellow' ? '#FFD400' : '#ef4444';
    } else {
      if (status === 'rød' || status === 'pending' || status === 'red') bgColor = '#ef4444';
      else if (status === 'gul' || status === 'in_progress' || status === 'yellow') bgColor = '#FFD400';
      else bgColor = '#22c55e';
    }
  } else if (type === 'drikkevand') {
    iconUrl = 'SVG_WATER'; bgColor = '#3b82f6';
  } else if (type === 'pausetelt') {
    iconUrl = 'SVG_TENT'; bgColor = '#8b5cf6';
  } else if (type === 'snack') {
    iconUrl = 'SVG_SNACK'; bgColor = '#f97316'; // orange
  } else if (type === 'kaffe_te') {
    iconUrl = 'SVG_COFFEE'; bgColor = '#92400e'; // mørk kaffebrun
  } else if (type === 'food') {
    iconUrl = 'SVG_FOOD'; bgColor = '#6b7280';
  } else if (type === 'drinks') {
    iconUrl = 'SVG_DRINKS'; bgColor = '#374151';
  } else if (type === 'evakueringspunkt') {
    iconUrl = 'SVG_EVAC'; bgColor = '#16a34a'; // ISO 7010 nødudgangs-grøn
  }
  
  // "Sæt i drift": turkis = i drift/OK, lilla = tømning bestilt (afventer/i gang).
  // Overskriver fase-farver, så en etableret ø skiller sig ud fra det røde opbygnings-kort.
  if (operational && (type === 'ressourceø' || type === 'ressourcespand')) {
    const pending = (status === 'rød' || status === 'red' || status === 'pending' ||
                     status === 'gul' || status === 'yellow' || status === 'in_progress');
    bgColor = pending ? '#a855f7' : '#14b8a6';
  }
  
  const baseIconSize = type === 'ressourcespand' ? 22 : 28;
  const iconSize = Math.round(baseIconSize * scale);
  const imgSize = type === 'ressourcespand' ? 0 : Math.round(18 * scale);
  const borderWidth = hasBlueRing ? Math.max(2, Math.round(3 * scale)) : Math.max(1, Math.round(2 * scale));
  const borderColor = hasBlueRing ? '#3b82f6' : 'white';
  const useWhiteFilter = type === 'ressourceø' || type === 'maxicontainer';
  
  const hasStativ = type === 'ressourcespand' && (
    bins && bins.some(b => (b.container_size || '').toLowerCase() === 'træstativ')
  );
  const hasDropzone = type === 'ressourceø' && (
    containers && containers.some(c => (c.fraction_type || '').toLowerCase() === 'dropzone')
  );
  const hasOutsideOperation = (
    (containers && containers.some(c => (c.fraction_type || '').toLowerCase() === 'tømmes udenfor afvikling')) ||
    (bins && bins.some(b => (b.fraction_type || '').toLowerCase() === 'tømmes udenfor afvikling'))
  );

  // Bestem hvilke markør-overlays der skal vises (custom markers + legacy fraction-baserede)
  const markerList = Array.isArray(markers) ? markers.filter(m => m && m.symbol) : [];
  const hasCustomDot = markerList.some(m => m.symbol === 'dot');
  const innerDot = (hasStativ || hasDropzone || hasCustomDot)
    ? (() => {
        // Vælg dot-farve: først custom marker, ellers default sort
        const dotMarker = markerList.find(m => m.symbol === 'dot');
        const dotColor = dotMarker ? dotMarker.color : '#000';
        return `<div style="position: absolute; width: ${Math.round(8 * scale)}px; height: ${Math.round(8 * scale)}px; background: ${dotColor}; border-radius: 50%; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 3;"></div>`;
      })()
    : '';

  // Build extra overlays for circle and triangle markers (stackable, hver med 4px tykkelse)
  let extraShadow = hasOutsideOperation ? `0 0 0 ${Math.max(3, Math.round(4 * scale))}px #a855f7` : '';
  const triangleOverlays = [];
  let circleIdx = 0;
  markerList.forEach(m => {
    if (m.symbol === 'circle') {
      // Stack ring uden om — hver ring 4px ud
      const offset = Math.max(3, Math.round(4 * scale)) * (circleIdx + 1) + (hasOutsideOperation ? Math.max(3, Math.round(4 * scale)) : 0);
      extraShadow += `${extraShadow ? ',' : ''} 0 0 0 ${offset}px ${m.color}`;
      circleIdx += 1;
    } else if (m.symbol === 'triangle') {
      const triSize = Math.round((type === 'ressourcespand' ? 36 : 44) * scale);
      triangleOverlays.push(
        `<svg style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); pointer-events:none; z-index:2;" width="${triSize}" height="${triSize}" viewBox="0 0 24 24"><polygon points="12,2 22.5,21.5 1.5,21.5" fill="none" stroke="${m.color}" stroke-width="1.8" stroke-linejoin="round" /></svg>`
      );
    }
  });
  const purpleRing = extraShadow ? `; box-shadow: ${extraShadow}` : '';
  const triangleHtml = triangleOverlays.join('');
  
  // Opmærksomhedspunkt
  if (type === 'opmærksomhedspunkt') {
    const attSize = Math.round(28 * scale);
    const attFontSize = Math.round(14 * scale);
    const html = `<div style="width:${attSize}px;height:${attSize}px;display:flex;align-items:center;justify-content:center;pointer-events:none;cursor:pointer;filter:drop-shadow(0 2px 3px rgba(0,0,0,0.5));">
      <svg width="${attSize}" height="${attSize}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L1 21h22L12 2z" fill="#1a1a1a" stroke="#000" stroke-width="0.5"/>
        <text x="12" y="17" text-anchor="middle" font-size="${attFontSize}" font-weight="bold" fill="#ef4444">!</text>
      </svg></div>`;
    const icon = L.divIcon({ html, className: 'custom-marker attention-marker', iconSize: [attSize, attSize], iconAnchor: [attSize / 2, attSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }

  // Evakueringspunkt (ISO 7010 E007 samlingspunkt) — stor grøn cirkel med 2 personer + 4 pile ind mod midten.
  // Bevidst 2× størrelse så det er synligt på afstand, med subtil pulserende ring.
  if (type === 'evakueringspunkt') {
    const evacSize = Math.round(56 * scale); // ~2× normal 28px
    const html = `<style>@keyframes evacPulse{0%,100%{box-shadow:0 0 0 0 rgba(22,163,74,0.6),0 3px 8px rgba(0,0,0,0.5);}50%{box-shadow:0 0 0 ${Math.round(8*scale)}px rgba(22,163,74,0);}}</style>
      <div style="width:${evacSize}px;height:${evacSize}px;border-radius:50%;background:#16a34a;border:${Math.max(2,Math.round(3*scale))}px solid white;display:flex;align-items:center;justify-content:center;pointer-events:none;cursor:pointer;animation:evacPulse 2s ease-in-out infinite;position:relative;">
        <svg width="${Math.round(evacSize*0.78)}" height="${Math.round(evacSize*0.78)}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <!-- 4 pile ind mod midten (ISO 7010 E007) -->
          <path d="M4 4 L22 22 M4 4 L4 14 M4 4 L14 4" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <path d="M96 4 L78 22 M96 4 L96 14 M96 4 L86 4" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <path d="M4 96 L22 78 M4 96 L4 86 M4 96 L14 96" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <path d="M96 96 L78 78 M96 96 L96 86 M96 96 L86 96" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <!-- To personer i midten (venstre + højre) -->
          <circle cx="40" cy="38" r="7" fill="white"/>
          <path d="M31 50 Q31 47 34 47 L46 47 Q49 47 49 50 L47 68 L44 68 L44 75 L36 75 L36 68 L33 68 Z" fill="white"/>
          <circle cx="60" cy="38" r="7" fill="white"/>
          <path d="M51 50 Q51 47 54 47 L66 47 Q69 47 69 50 L67 68 L64 68 L64 75 L56 75 L56 68 L53 68 Z" fill="white"/>
        </svg>
      </div>`;
    const icon = L.divIcon({ html, className: 'custom-marker evacuation-marker', iconSize: [evacSize, evacSize], iconAnchor: [evacSize / 2, evacSize / 2] });
    iconCache.set(cacheKey, icon);
    return icon;
  }
  
  // SVG-baserede ikoner (drikkevand, pausetelt, snack, kaffe_te, food, drinks, pissoir, toilet, ressourceø, maxicontainer)
  const svgIcons = {
    drikkevand: (s) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C12 2 5 10.5 5 15C5 18.87 8.13 22 12 22C15.87 22 19 18.87 19 15C19 10.5 12 2 12 2Z" fill="white" stroke="rgba(255,255,255,0.5)" stroke-width="0.5"/></svg>`,
    pausetelt: (s) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 3L2 19H22L12 3Z" fill="white" stroke="rgba(255,255,255,0.5)" stroke-width="0.5"/><path d="M12 10V19" stroke="rgba(100,100,100,0.5)" stroke-width="1"/><path d="M8 19V14L12 10L16 14V19" fill="rgba(200,200,200,0.3)" stroke="rgba(100,100,100,0.5)" stroke-width="0.5"/></svg>`,
    snack: (s) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M5 8 L19 8 L18 21 L6 21 Z" fill="white" stroke="rgba(255,255,255,0.5)" stroke-width="0.5"/><path d="M7 4 L17 4 L18 8 L6 8 Z" fill="white" stroke="rgba(150,150,150,0.6)" stroke-width="0.5"/><circle cx="9" cy="13" r="0.8" fill="rgba(150,90,40,0.7)"/><circle cx="13" cy="15" r="0.8" fill="rgba(150,90,40,0.7)"/><circle cx="11" cy="11" r="0.8" fill="rgba(150,90,40,0.7)"/><circle cx="15" cy="12" r="0.8" fill="rgba(150,90,40,0.7)"/><circle cx="10" cy="17" r="0.8" fill="rgba(150,90,40,0.7)"/></svg>`,
    kaffe_te: (s) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 8 L4 17 Q4 20 7 20 L15 20 Q18 20 18 17 L18 8 Z" fill="white" stroke="rgba(255,255,255,0.5)" stroke-width="0.5"/><path d="M18 10 Q22 10 22 13 Q22 16 18 16" fill="none" stroke="white" stroke-width="1.4"/><path d="M8 4 Q8 6 9 6 Q10 6 10 4" fill="none" stroke="white" stroke-width="1.2" stroke-linecap="round"/><path d="M12 4 Q12 6 13 6 Q14 6 14 4" fill="none" stroke="white" stroke-width="1.2" stroke-linecap="round"/></svg>`,
    food: (s) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M2 16h20v2c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-2z" fill="white"/><path d="M20 12H4c0-1.5.5-2.8 1.4-3.8C6.3 7.2 8 6 10 6c0-1.1.9-2 2-2s2 .9 2 2c2 0 3.7 1.2 4.6 2.2.9 1 1.4 2.3 1.4 3.8z" fill="white"/><rect x="2" y="13" width="20" height="2" rx="1" fill="rgba(200,200,200,0.8)"/></svg>`,
    drinks: (s) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M7 2l-1.5 14h11L15 2H7z" fill="white" stroke="rgba(255,255,255,0.5)" stroke-width="0.5"/><path d="M10 16v4" stroke="white" stroke-width="2"/><path d="M7 22h10" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M8 6h8" stroke="rgba(200,200,200,0.6)" stroke-width="1"/></svg>`,
  };

  // Toilet — firkantet hvidt skilt med sort mand+kvinde-silhouette (WC-standard skilt)
  if (type === 'toilet') {
    const tSize = iconSize;
    const bord = Math.max(1, Math.round(1.5 * scale));
    const rad = Math.max(2, Math.round(3 * scale));
    const html = `<div style="width:${tSize}px;height:${tSize}px;background:#fff;border:${bord}px solid #000;border-radius:${rad}px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.4)${purpleRing};position:relative;pointer-events:none;cursor:pointer;">
      <svg width="${Math.round(tSize*0.78)}" height="${Math.round(tSize*0.78)}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <!-- Kvinde til venstre -->
        <circle cx="30" cy="15" r="9" fill="#000"/>
        <path d="M20 26 L40 26 L46 62 L37 62 L37 88 L23 88 L23 62 L14 62 Z" fill="#000"/>
        <!-- Mand til højre -->
        <circle cx="70" cy="15" r="9" fill="#000"/>
        <rect x="60" y="26" width="20" height="35" rx="2" fill="#000"/>
        <rect x="62" y="59" width="7" height="30" fill="#000"/>
        <rect x="71" y="59" width="7" height="30" fill="#000"/>
      </svg>${innerDot}${triangleHtml}</div>`;
    const icon = L.divIcon({ html, className: 'custom-marker toilet-marker', iconSize: [tSize, tSize], iconAnchor: [tSize / 2, tSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }

  // Pissoir — firkantet hvidt skilt med sort mand-ved-urinal silhouette
  if (type === 'pissoir') {
    const pSize = iconSize;
    const bord = Math.max(1, Math.round(1.5 * scale));
    const rad = Math.max(2, Math.round(3 * scale));
    const html = `<div style="width:${pSize}px;height:${pSize}px;background:#fff;border:${bord}px solid #000;border-radius:${rad}px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.4)${purpleRing};position:relative;pointer-events:none;cursor:pointer;">
      <svg width="${Math.round(pSize*0.78)}" height="${Math.round(pSize*0.78)}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <!-- Væg + urinal på venstre side -->
        <rect x="10" y="8" width="6" height="80" fill="#000"/>
        <path d="M18 40 L36 40 Q40 40 40 44 L40 62 Q40 70 32 74 L18 74 Z" fill="#000"/>
        <!-- Mand til højre -->
        <circle cx="66" cy="20" r="9" fill="#000"/>
        <path d="M56 30 L76 30 L76 63 L70 63 L70 88 L60 88 L60 63 L56 63 Z" fill="#000"/>
        <!-- Stream/dråber -->
        <circle cx="50" cy="55" r="1.5" fill="#000"/>
        <circle cx="47" cy="60" r="1.2" fill="#000"/>
        <circle cx="43" cy="64" r="1" fill="#000"/>
      </svg>${innerDot}${triangleHtml}</div>`;
    const icon = L.divIcon({ html, className: 'custom-marker pissoir-marker', iconSize: [pSize, pSize], iconAnchor: [pSize / 2, pSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }
  
  // Maxicontainer-special: pulsering + kryds. Bruger PNG-ikon (cached, kendt visuelt udtryk)
  if (type === 'maxicontainer') {
    const shouldPulse = status === 'pending' || status === '75_pct';
    const isOutOfService = status === 'out_of_service';
    const crossOverlay = isOutOfService ? `<svg style="position:absolute;top:0;left:0;width:100%;height:100%;" viewBox="0 0 28 28"><line x1="6" y1="6" x2="22" y2="22" stroke="black" stroke-width="3" stroke-linecap="round"/><line x1="22" y1="6" x2="6" y2="22" stroke="black" stroke-width="3" stroke-linecap="round"/></svg>` : '';
    const pulseAnimation = shouldPulse ? 'animation: pulse 1.5s ease-in-out infinite;' : '';
    const html = `<style>@keyframes pulse{0%,100%{transform:scale(1);opacity:1;}50%{transform:scale(1.15);opacity:0.9;}}</style>
      <div style="background:${bgColor};width:${iconSize}px;height:${iconSize}px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:${borderWidth}px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)${purpleRing};position:relative;pointer-events:none;cursor:pointer;${pulseAnimation}">
        <img src="${iconUrl}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;pointer-events:none;filter:brightness(0) invert(1);" />${innerDot}${triangleHtml}${crossOverlay}</div>`;
    const icon = L.divIcon({ html, className: 'custom-marker maxicontainer-marker', iconSize: [iconSize, iconSize], iconAnchor: [iconSize / 2, iconSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }

  if (svgIcons[type]) {
    const svgS = Math.round(18 * scale);
    const html = `<div style="background:${bgColor};width:${iconSize}px;height:${iconSize}px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:${borderWidth}px solid ${borderColor};box-shadow:0 2px 6px rgba(0,0,0,0.4)${purpleRing};position:relative;pointer-events:none;cursor:pointer;">${svgIcons[type](svgS)}${innerDot}${triangleHtml}</div>`;
    const icon = L.divIcon({ html, className: `custom-marker ${type}-marker`, iconSize: [iconSize, iconSize], iconAnchor: [iconSize / 2, iconSize] });
    iconCache.set(cacheKey, icon);
    return icon;
  }
  
  // Default (ressourceø med PNG, ressourcespand uden ikon, etc.)
  const imgHtml = iconUrl ? `<img src="${iconUrl}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;pointer-events:none;${useWhiteFilter ? 'filter:brightness(0) invert(1);' : ''}" />` : '';
  const html = `<div style="background:${bgColor};width:${iconSize}px;height:${iconSize}px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:${borderWidth}px solid ${borderColor};box-shadow:0 2px 6px rgba(0,0,0,0.4)${purpleRing};position:relative;pointer-events:none;cursor:pointer;">${imgHtml}${innerDot}${triangleHtml}</div>`;
  const icon = L.divIcon({ html, className: 'custom-marker', iconSize: [iconSize, iconSize], iconAnchor: [iconSize / 2, iconSize] });
  iconCache.set(cacheKey, icon);
  return icon;
};

export function createTrackerIcon(color = '#e11d48', trackerType = 'vehicle') {
  const svgKey = trackerType === 'cart' ? 'cart' : trackerType === 'person' ? 'person' : 'car';
  return new L.DivIcon({
    html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);">${TRACKER_SVG[svgKey]}</div>`,
    className: 'vehicle-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

// Ryd ikon-cache (kan bruges ved zoom-ændring for at frigøre hukommelse)
export function clearIconCache() {
  iconCache.clear();
}

// Cluster ikon: Farven afspejler den "værste" status (rød > gul/orange > grøn)
export function createClusterIcon(cluster) {
  const markers = cluster.getAllChildMarkers();
  let maxPriority = 0;
  markers.forEach(m => {
    const p = m.options?.statusPriority || 0;
    if (p > maxPriority) maxPriority = p;
  });
  const count = cluster.getChildCount();
  const color = maxPriority >= 2 ? '#ef4444' : maxPriority >= 1 ? '#FFD400' : '#22c55e';
  const borderColor = maxPriority >= 2 ? '#dc2626' : maxPriority >= 1 ? '#CC9F00' : '#16a34a';
  const size = count >= 50 ? 48 : count >= 20 ? 42 : count >= 10 ? 38 : 34;
  
  return L.divIcon({
    html: `<div style="
      background: ${color};
      width: ${size}px; height: ${size}px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      color: white; font-weight: bold; font-size: ${size > 40 ? 14 : 12}px;
      border: 3px solid ${borderColor};
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    ">${count}</div>`,
    className: 'marker-cluster-custom',
    iconSize: L.point(size, size, true),
  });
}
